import * as React from "react";
import { Divider, GridItem } from "@chakra-ui/react";
import {
  Form,
  AccordionItem,
  Select,
  TextInput,
  VStack,
  Grid
} from "components";
import { useCurrentCase } from "duck";
import { OcclusionEnum } from "requests";
import { useUpdateGeneralAssessment } from "../../duck";
import { AllNormalToggle, DentalChart } from "./components";
import {
  PERIODONTAL_DISEASE_OPTIONS,
  PLAQUE_INDEX_OPTIONS,
  CALCULUS_INDEX_OPTIONS,
  GINGIVAL_INDEX,
  SKULL_TYPE_OPTIONS,
  ORAL_STATE_OPTIONS,
  ORAL_STATE_BOOLEAN_OPTIONS,
  OCCLUSION_OPTIONS,
  OCCLUSION_DESCRIPTION_VALUES,
  getDefaultValues,
  FormValues
} from "./duck";

const GeneralAssessment: React.FC = () => {
  const generalAssessment = useCurrentCase(
    ({ data }) => data.client.patient.generalAssessment
  );
  const defaultValues = React.useMemo(
    () => getDefaultValues(generalAssessment),
    [generalAssessment]
  );

  const onChange = useUpdateGeneralAssessment();

  return (
    <AccordionItem title="General Assessment">
      <Form<FormValues> defaultValues={defaultValues}>
        {({ setValue }) => (
          <>
            <Form.Watcher defaultValues={defaultValues} onChange={onChange} />
            <VStack spacing="24px">
              <Grid
                templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                width="100%"
              >
                <DentalChart />

                <GridItem>
                  <Select
                    name="skullType"
                    options={SKULL_TYPE_OPTIONS}
                    placeholder="Select"
                    label="Skull Type"
                  />
                </GridItem>
              </Grid>

              <Divider h="1px" borderColor="#D0D1D3" />

              <AllNormalToggle />

              <Grid
                templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                width="100%"
              >
                <GridItem>
                  <Select
                    name="extraoral"
                    options={ORAL_STATE_OPTIONS}
                    placeholder="Select"
                    label="Extraoral"
                  />
                </GridItem>

                <GridItem>
                  <Select
                    name="lymphNodes"
                    options={ORAL_STATE_OPTIONS}
                    placeholder="Select"
                    label="Lymph Nodes"
                  />
                </GridItem>

                <GridItem>
                  <Select
                    name="tonsils"
                    options={ORAL_STATE_OPTIONS}
                    placeholder="Select"
                    label="Tonsils"
                  />
                </GridItem>

                <GridItem>
                  <Select
                    name="softPalate"
                    options={ORAL_STATE_OPTIONS}
                    placeholder="Select"
                    label="Soft Palate"
                  />
                </GridItem>

                <GridItem>
                  <Select
                    name="hardPalate"
                    options={ORAL_STATE_OPTIONS}
                    placeholder="Select"
                    label="Hard Palate"
                  />
                </GridItem>

                <GridItem>
                  <Select
                    name="buccalMucosa"
                    options={ORAL_STATE_OPTIONS}
                    placeholder="Select"
                    label="Buccal Mucosa"
                  />
                </GridItem>

                <GridItem>
                  <Select
                    name="caudalOralMucosa"
                    options={ORAL_STATE_OPTIONS}
                    placeholder="Select"
                    label="Caudal Oral Mucosa"
                  />
                </GridItem>

                <GridItem>
                  <Select
                    name="tongue"
                    options={ORAL_STATE_OPTIONS}
                    placeholder="Select"
                    label="Tongue"
                  />
                </GridItem>

                <GridItem>
                  <Select
                    name="fistula"
                    options={ORAL_STATE_BOOLEAN_OPTIONS}
                    placeholder="Select"
                    label="Fistula"
                  />
                </GridItem>

                <GridItem>
                  <Select
                    name="parulisSinusTract"
                    options={ORAL_STATE_BOOLEAN_OPTIONS}
                    placeholder="Select"
                    label="Parulis/Sinus Tract"
                  />
                </GridItem>

                <GridItem>
                  <Select<OcclusionEnum>
                    name="occlusion"
                    options={OCCLUSION_OPTIONS}
                    placeholder="Select"
                    label="Occlusion"
                    onChange={value => {
                      setValue("occlusion", value);
                      setValue(
                        "occlusionDescription",
                        value ? OCCLUSION_DESCRIPTION_VALUES[value] : ""
                      );
                    }}
                  />
                </GridItem>
              </Grid>

              <TextInput
                type="text-area"
                name="occlusionDescription"
                placeholder="Select"
                label="Occlusion"
              />

              <Grid
                templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                width="100%"
              >
                <GridItem>
                  <Select
                    name="periodontalDisease"
                    options={PERIODONTAL_DISEASE_OPTIONS}
                    placeholder="Periodontal Disease"
                    label="Periodontal Disease"
                  />
                </GridItem>

                <GridItem>
                  <Select
                    name="plaqueIndex"
                    options={PLAQUE_INDEX_OPTIONS}
                    placeholder="Plaque Index"
                    label="Plaque Index"
                  />
                </GridItem>

                <GridItem>
                  <Select
                    name="calculusIndex"
                    options={CALCULUS_INDEX_OPTIONS}
                    placeholder="Calculus Index"
                    label="Calculus Index"
                  />
                </GridItem>

                <GridItem>
                  <Select
                    name="gingivalIndex"
                    options={GINGIVAL_INDEX}
                    placeholder="Gingival Index"
                    label="Gingival Index"
                  />
                </GridItem>
              </Grid>

              <TextInput
                name="additionalCommentsAndNotes"
                type="text-area"
                label="Additional Findings, Comments and Notes"
                placeholder="Additional Findings, Comments and Notes"
              />
            </VStack>
          </>
        )}
      </Form>
    </AccordionItem>
  );
};

export default GeneralAssessment;
